import { PropsWithChildren, ReactElement, useEffect, useRef, useState } from "react";
import { LngLatLike, MarkerOptions, EventData, Marker as MapMarker } from "mapbox-gl";
import { MapService } from "../../../services/map/MapService";
import { useDependency } from "../../../contexts/DependencyContext";
import { useEventListener } from "../../../hooks/UseEventListener";
import styles from "./Marker.module.scss";

interface MarkerProps
{
    id: string | number;
    position: LngLatLike;
    options?: MarkerOptions;
    popup?: { html: string, visible?: boolean }
    onClick?: (e?: EventData) => void;
    onDblClick?: (e?: EventData) => void;
    onMouseDown?: (e?: EventData) => void;
    onMouseUp?: (e?: EventData) => void;
    onMouseMove?: (e?: EventData) => void;
    onMouseEnter?: (e?: EventData) => void;
    onMouseLeave?: (e?: EventData) => void;
    onMouseOver?: (e?: EventData) => void;
    onMouseOut?: (e?: EventData) => void;
    onContextMenu?: (e?: EventData) => void;
    onTouchStart?: (e?: EventData) => void;
    onTouchEnd?: (e?: EventData) => void;
    onTouchCancel?: (e?: EventData) => void;
}

const Marker = (props: PropsWithChildren<MarkerProps>): ReactElement =>
{
    const mapService = useDependency(MapService);
    const [popupVisible, setPopupVisibility] = useState(false);
    const marker = useRef<MapMarker>(null as any);
    const markerButtonRef = useRef<HTMLButtonElement>(null as any);

    const handleButtonClick = (e: EventData): void =>
    {
        if (props.onClick) props.onClick(e);
        setPopupVisibility(!popupVisible);
    };

    const handleOutsideClick = (e: PointerEvent): void =>
    {
        const target = e.target as HTMLElement;

        if (popupVisible && markerButtonRef.current && !markerButtonRef.current.contains(target))
            setPopupVisibility(false);
    }

    useEffect(() =>
    {
        if (markerButtonRef.current)
            marker.current = mapService.setMarker(markerButtonRef.current, props.id, props.position, props.options);

        return () => mapService.removeMarker(props.id);
    }, [mapService, props]);

    useEventListener("pointerdown", handleOutsideClick);

    return (
        <button
            ref={markerButtonRef}
            className={styles.marker}
            onMouseDown={props.onMouseDown}
            onMouseUp={props.onMouseUp}
            onMouseMove={props.onMouseMove}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            onMouseOver={props.onMouseOver}
            onMouseOut={props.onMouseOut}
            onContextMenu={props.onContextMenu}
            onTouchStart={props.onTouchStart}
            onTouchEnd={props.onTouchEnd}
            onTouchCancel={props.onTouchCancel}
            onClick={handleButtonClick}
        >
            <i className="material-icons" style={{ color: props.options?.color }}>place</i>
        </button>
    );
};

export default Marker;
